/**
 * PermissionsContext — Provides the current user's full role permissions
 * to all child components. Fetches from GET /api/users/me/role in live mode,
 * falls back to mock data in demo/tour mode or on API error.
 */

import { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from 'react';
import { getDataMode, fetchCurrentUserRole } from '@/lib/data-service';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/providers/AuthProvider';

import type {
  RolePermissions,
  ProjectVisibility,
  ProjectActions,
  ProjectTabPermissions,
  ProjectTabAccess,
  ProjectTabKey,
  ReportPermissions,
  SettingsPermissions,
} from '@/types/roles.types';

/** localStorage key written by the role-editor after a successful save. */
export const PERMISSIONS_UPDATED_KEY = 'evo_permissions_updated';

// ── Default (admin) permissions — used as fallback ────────────────────────────

const DEFAULT_PERMISSIONS: RolePermissions = {
  projectVisibility: 'all',
  projectActions: {
    create: true,
    edit: true,
    delete: true,
    changeStatus: true,
    archive: true,
    assignKeyPeople: true,
  },
  projectTabs: {
    overview: 'Edit',
    financials: 'Edit',
    billing: 'Edit',
    salesBudget: 'Edit',
    costBudget: 'Edit',
    resourceBudget: 'Edit',
    costCentres: 'Edit',
    purchases: 'Edit',
    time: 'Edit',
    expenses: 'Edit',
    team: 'Edit',
    resourcing: 'Edit',
    milestones: 'Edit',
    changeRequests: 'Edit',
    audit: 'Edit',
    reports: 'Edit',
  },
  reports: {
    executiveDashboard: true,
    pmDashboard: true,
    projectTeamDashboard: true,
    plReport: true,
    wipReport: true,
    utilisationReport: true,
    budgetVarianceReport: true,
    auditLogReport: true,
    projectTransactionsReport: true,
    projectStatusReport: true,
    projectForecastReport: true,
    profitabilityReport: true,
  },
  settings: {
    generalSettings: true,
    configureAuditTrail: true,
    projectSettings: true,
    integrations: true,
    rolesAndPermissions: true,
    templates: true,
    notifications: true,
    viewAuditLogReport: true,
    viewProjectAuditTab: true,
  },
};

// Deny-by-default base for report permissions so that any key absent from the
// API response is treated as "no access" rather than inheriting the admin-level
// DEFAULT_PERMISSIONS (which are all true).  This prevents newly-added report
// permission keys from being implicitly granted to roles whose stored JSON
// predates those keys.
const DENY_ALL_REPORTS: ReportPermissions = {
  executiveDashboard: false,
  pmDashboard: false,
  projectTeamDashboard: false,
  plReport: false,
  wipReport: false,
  utilisationReport: false,
  budgetVarianceReport: false,
  auditLogReport: false,
  projectTransactionsReport: false,
  projectStatusReport: false,
  projectForecastReport: false,
  profitabilityReport: false,
};

function mergePermissions(fetched: RolePermissions): RolePermissions {
  // For settings, default missing keys to false (deny access) rather than
  // inheriting the all-true admin defaults. This prevents newly-added
  // permission keys from granting access to roles that were saved before
  // those keys existed in the stored JSON.
  const restrictiveSettings: SettingsPermissions = {
    generalSettings: false,
    configureAuditTrail: false,
    projectSettings: false,
    integrations: false,
    rolesAndPermissions: false,
    templates: false,
    notifications: false,
    viewAuditLogReport: false,
    viewProjectAuditTab: false,
  };

  return {
    ...DEFAULT_PERMISSIONS,
    ...fetched,
    projectActions: {
      ...DEFAULT_PERMISSIONS.projectActions,
      ...fetched.projectActions,
    },
    projectTabs: {
      ...DEFAULT_PERMISSIONS.projectTabs,
      ...fetched.projectTabs,
    },
    reports: {
      ...DENY_ALL_REPORTS,
      ...fetched.reports,
    },
    settings: {
      ...restrictiveSettings,
      ...fetched.settings,
    },
  };
}

// ── Context value type ────────────────────────────────────────────────────────

export interface PermissionsContextValue {
  role: string;
  permissions: RolePermissions;
  isLoading: boolean;
  isMock: boolean;

  // Convenience accessors
  projectVisibility: ProjectVisibility;
  projectActions: ProjectActions;
  projectTabs: ProjectTabPermissions;
  reports: ReportPermissions;
  settings: SettingsPermissions;

  // Helper functions
  canPerformAction: (action: keyof ProjectActions) => boolean;
  getTabAccess: (tab: ProjectTabKey) => ProjectTabAccess;
  isTabVisible: (tab: ProjectTabKey) => boolean;
  isTabEditable: (tab: ProjectTabKey) => boolean;
  canAccessSetting: (setting: keyof SettingsPermissions) => boolean;

  /** Re-fetch the current user's permissions from the API. */
  refetchPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { currentCompany } = useCompany();
  const { authenticatedUser } = useAuth();
  const [role, setRole] = useState<string>('administrator');
  const [permissions, setPermissions] = useState<RolePermissions>(DEFAULT_PERMISSIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [isMock, setIsMock] = useState(true);

  const loadPermissions = useCallback(async () => {
    const mode = getDataMode();
    if (mode === 'demo' || mode === 'tour') {
      setIsLoading(false);
      return;
    }

    try {
      const { role: fetchedRole, permissions: fetchedPermissions } = await fetchCurrentUserRole();
      setRole(fetchedRole);
      setPermissions(mergePermissions(fetchedPermissions));
      setIsMock(false);
    } catch (err) {
      console.error('Failed to fetch user permissions, using defaults:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch permissions once a company is selected (ensures X-Company-Id is
  // set on the request) and re-fetch when the active company changes.
  useEffect(() => {
    if (!currentCompany) return;
    loadPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the company *id* changes, not the whole object
  }, [loadPermissions, currentCompany?.id]);

  // When no company is selected but the user is an Administrator, resolve
  // loading with default (full-access) permissions so admin-only pages
  // like Settings > Integrations remain reachable.
  useEffect(() => {
    if (currentCompany) return;
    const mode = getDataMode();
    if (mode === 'demo' || mode === 'tour') return;
    if (!authenticatedUser) return;
    if (authenticatedUser.applicationRole === 'Administrator') {
      setIsLoading(false);
    }
  }, [currentCompany, authenticatedUser]);

  // Track the last invalidation timestamp we have already processed so we
  // don't refetch more than once for the same save event.
  const lastProcessedRef = useRef<string | null>(null);

  // Re-fetch when another tab writes the invalidation signal to localStorage.
  // The `storage` event only fires on *other* tabs/windows of the same origin.
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key !== PERMISSIONS_UPDATED_KEY || !e.newValue) return;
      if (e.newValue === lastProcessedRef.current) return;
      lastProcessedRef.current = e.newValue;
      loadPermissions();
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [loadPermissions]);

  // Re-fetch when the tab becomes visible again (user switches back to it).
  // This covers the case where a role was saved in a different browser or
  // session — the user sees the updated permissions as soon as they return.
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState !== 'visible') return;
      const stamp = localStorage.getItem(PERMISSIONS_UPDATED_KEY);
      if (stamp && stamp !== lastProcessedRef.current) {
        lastProcessedRef.current = stamp;
        loadPermissions();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadPermissions]);

  const value = useMemo<PermissionsContextValue>(() => {
    const canPerformAction = (action: keyof ProjectActions) => permissions.projectActions[action];
    const getTabAccess = (tab: ProjectTabKey) => permissions.projectTabs[tab];
    const isTabVisible = (tab: ProjectTabKey) => permissions.projectTabs[tab] !== 'Hidden';
    const isTabEditable = (tab: ProjectTabKey) => permissions.projectTabs[tab] === 'Edit';
    const canAccessSetting = (setting: keyof SettingsPermissions) => permissions.settings[setting];

    return {
      role,
      permissions,
      isLoading,
      isMock,
      projectVisibility: permissions.projectVisibility,
      projectActions: permissions.projectActions,
      projectTabs: permissions.projectTabs,
      reports: permissions.reports,
      settings: permissions.settings,
      canPerformAction,
      getTabAccess,
      isTabVisible,
      isTabEditable,
      canAccessSetting,
      refetchPermissions: loadPermissions,
    };
  }, [role, permissions, isLoading, isMock, loadPermissions]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePermissions(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return ctx;
}
